import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ROUTES } from "@/routes/path"
import { ErrorCard } from "@/components/ui/error"
import { useToast } from "@/hooks/useToast"
import { useAuth, useForgotPassword, useSetEmail } from "@/hooks/useAuth"

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
})

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>

export default function ForgotPassword() {
  const [formError, setFormError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { toast } = useToast()
  const setEmail = useSetEmail()
  const { email } = useAuth()

  const mutation = useForgotPassword({
    onSuccess: () => {
      toast({
        title: "Reset code sent",
        description: "If an account exists with that email, check your inbox for the reset code.",
        variant: "success",
      })
      navigate(ROUTES.AUTH.RESET_PASSWORD_OTP)
    },
    onError: (error: any) => {
      setFormError(error?.response?.data?.error || "Failed to send reset email")
    },
  })

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, touchedFields },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: email || "",
    },
  })

  const emailValue = watch("email")

  useEffect(() => {
    setEmail(emailValue)
  }, [emailValue, setEmail])

  return (
    <div className="relative flex h-screen flex-col items-center bg-gray-50 dark:bg-zinc-900">
      <div className="relative z-10 flex w-full flex-col items-center">
        <div className="my-10 flex justify-center">
          <img src="/logo.svg" alt="Ledgerbeam" width={220} height={50} />
        </div>
        {/* Card */}
        <div className="mx-2 max-h-fit w-full max-w-lg rounded-xl bg-white p-8 shadow-xl dark:bg-zinc-800">
          <h1 className="mb-8 text-2xl font-semibold dark:text-zinc-100">Forgot your password?</h1>
          <p className="mb-6 text-gray-600 dark:text-gray-400">
            Enter your email address and we&apos;ll send you a code to reset your password if an account exists.
          </p>
          <form
            onSubmit={handleSubmit((data: ForgotPasswordFormValues) => mutation.mutate(data.email))}
            className="space-y-6"
          >
            <div className="grid gap-4">
              <Input
                id="email"
                placeholder="name@company.com"
                type="email"
                label="Email"
                disabled={mutation.isPending}
                {...register("email")}
                error={errors.email?.message}
                touched={touchedFields.email}
              />
            </div>
            {formError && <ErrorCard message={formError} />}
            <Button type="submit" disabled={mutation.isPending} className="w-full">
              {mutation.isPending ? "Sending..." : "Send Reset Code"}
            </Button>
          </form>
        </div>

        <div className="-mt-4 w-full max-w-lg rounded-b-xl border-t bg-gray-50 px-8 py-4 text-center text-sm text-muted-foreground shadow-md shadow-gray-200 dark:border-zinc-700 dark:bg-zinc-800 dark:shadow-zinc-700">
          Remember your password?{" "}
          <Link to={ROUTES.AUTH.LOGIN} className="font-medium text-primary hover:underline dark:text-primary">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
